import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import employeeService from '../../../services/employeeService';
import NotificationToast from '../../../components/ui/NotificationToast';
import apiClient from '../../../lib/apiClient';

const ScheduleManagement = ({ isAdmin, clientId }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const daysOfWeek = [
    { value: 0, label: 'Sunday', short: 'Sun' },
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' }
  ];

  useEffect(() => {
    loadEmployees();
  }, [clientId]);

  useEffect(() => {
    if (selectedEmployee) {
      loadSchedule(selectedEmployee);
    } else {
      setSchedule({});
    }
  }, [selectedEmployee]);

  const loadEmployees = async () => {
    try {
      const filters = {};
      if (clientId) {
        filters.client_id = clientId;
      }
      const result = await employeeService.getEmployees(filters);
      if (result?.success) {
        setEmployees(result.data || []);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadSchedule = async (employeeId) => {
    setLoading(true);
    try {
      const result = await apiClient.get(`/schedules?employee_id=${employeeId}`);
      if (result?.success) {
        const schedules = result.data || [];
        const scheduleMap = {};
        schedules.forEach(s => {
          scheduleMap[s.day_of_week] = {
            is_working_day: s.is_working_day,
            shift_start_time: s.shift_start_time ? s.shift_start_time.substring(0, 5) : '',
            shift_end_time: s.shift_end_time ? s.shift_end_time.substring(0, 5) : '',
            is_week_off: s.is_week_off
          };
        });
        setSchedule(scheduleMap);
      }
    } catch (error) {
      // If no schedule exists, initialize with defaults
      const defaultSchedule = {};
      daysOfWeek.forEach(day => {
        defaultSchedule[day.value] = {
          is_working_day: day.value !== 0 && day.value !== 6, // Mon-Fri default
          shift_start_time: '09:00',
          shift_end_time: '18:00',
          is_week_off: day.value === 0 || day.value === 6
        };
      });
      setSchedule(defaultSchedule);
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (day, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedEmployee) {
      setNotification({ message: 'Please select an employee', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const schedules = daysOfWeek.map(day => ({
        employee_id: selectedEmployee,
        day_of_week: day.value,
        is_working_day: schedule[day.value]?.is_working_day || false,
        shift_start_time: schedule[day.value]?.shift_start_time || null,
        shift_end_time: schedule[day.value]?.shift_end_time || null,
        is_week_off: schedule[day.value]?.is_week_off || false
      }));

      const result = await apiClient.post('/schedules/bulk', { employee_id: selectedEmployee, schedules });
      if (result?.success) {
        setNotification({ message: 'Schedule saved successfully', type: 'success' });
        loadSchedule(selectedEmployee);
      } else {
        setNotification({ message: result?.error || 'Failed to save schedule', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred while saving schedule', type: 'error' });
      console.error('Error saving schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyFrom = async (sourceEmployeeId) => {
    if (!sourceEmployeeId || !selectedEmployee) return;

    setLoading(true);
    try {
      const result = await apiClient.get(`/schedules?employee_id=${sourceEmployeeId}`);
      if (result?.success) {
        const schedules = result.data || [];
        const scheduleMap = {};
        schedules.forEach(s => {
          scheduleMap[s.day_of_week] = {
            is_working_day: s.is_working_day,
            shift_start_time: s.shift_start_time ? s.shift_start_time.substring(0, 5) : '',
            shift_end_time: s.shift_end_time ? s.shift_end_time.substring(0, 5) : '',
            is_week_off: s.is_week_off
          };
        });
        setSchedule(scheduleMap);
        setNotification({ message: 'Schedule copied successfully', type: 'success' });
      }
    } catch (error) {
      setNotification({ message: 'Failed to copy schedule', type: 'error' });
      console.error('Error copying schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="mb-6">
        <h4 className="text-lg font-semibold text-foreground mb-2">Employee Work Schedules</h4>
        <p className="text-sm text-muted-foreground">Set working days and shift timings for employees</p>
      </div>

      {/* Employee Selection */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Select Employee *</label>
            <Select
              value={selectedEmployee}
              onValueChange={setSelectedEmployee}
              placeholder="Select Employee"
              options={[
                { value: '', label: 'Select Employee' },
                ...(employees.map(emp => ({
                  value: emp.id,
                  label: `${emp.full_name} (${emp.employee_id})`
                })) || [])
              ]}
            />
          </div>
          {selectedEmployee && (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Copy From</label>
                <Select
                  value=""
                  onValueChange={handleCopyFrom}
                  placeholder="Copy schedule from..."
                  options={[
                    { value: '', label: 'Select Employee' },
                    ...(employees
                      .filter(emp => emp.id !== selectedEmployee)
                      .map(emp => ({
                        value: emp.id,
                        label: `${emp.full_name} (${emp.employee_id})`
                      })) || [])
                  ]}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleSave} disabled={loading} className="w-full">
                  {loading ? 'Saving...' : 'Save Schedule'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Schedule Table */}
      {selectedEmployee ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Day</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Working Day</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Week Off</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Start Time</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase">End Time</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {daysOfWeek.map(day => {
                const daySchedule = schedule[day.value] || {
                  is_working_day: false,
                  shift_start_time: '09:00',
                  shift_end_time: '18:00',
                  is_week_off: false
                };
                return (
                  <tr key={day.value} className="hover:bg-muted/50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-foreground">
                      {day.label}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={daySchedule.is_working_day}
                        onChange={(e) => {
                          const isWorking = e.target.checked;
                          handleDayChange(day.value, 'is_working_day', isWorking);
                          if (isWorking) {
                            handleDayChange(day.value, 'is_week_off', false);
                          }
                        }}
                        disabled={!isAdmin}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={daySchedule.is_week_off}
                        onChange={(e) => {
                          const isWeekOff = e.target.checked;
                          handleDayChange(day.value, 'is_week_off', isWeekOff);
                          if (isWeekOff) {
                            handleDayChange(day.value, 'is_working_day', false);
                          }
                        }}
                        disabled={!isAdmin}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Input
                        type="time"
                        value={daySchedule.shift_start_time || '09:00'}
                        onChange={(e) => handleDayChange(day.value, 'shift_start_time', e.target.value)}
                        disabled={!isAdmin || !daySchedule.is_working_day}
                        className="w-32 mx-auto"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Input
                        type="time"
                        value={daySchedule.shift_end_time || '18:00'}
                        onChange={(e) => handleDayChange(day.value, 'shift_end_time', e.target.value)}
                        disabled={!isAdmin || !daySchedule.is_working_day}
                        className="w-32 mx-auto"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Select an employee to view and edit their work schedule</p>
        </div>
      )}
    </div>
  );
};

export default ScheduleManagement;

